import Footer from "./Footer";
import Icon, { Close } from "@/components/icons";
import ProductSearchListItem from "./ProductSearchListItem";
import { ProductSearchListItemProps } from "./ProductSearchListItem";
import MainContent from "./MainContent";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { getCorrelationID } from "@/utils/tagular/helpers";
import { FormatSubType } from "@/constants/tagular/main";
import { useEffect, useRef, useState } from "react";
import { useProductViewStorage } from "@/hooks/useProductViewStorage";

type DefaultStateProps = {
  recentlyViewed: ProductSearchListItemProps[];
  featuredItems: ProductSearchListItemProps[];
  location?: string;
};

const DefaultState = ({
  recentlyViewed,
  featuredItems,
  location = "embedded-search",
}: DefaultStateProps) => {
  const hasViewedRef = useRef(false);
  const { removeProductView } = useProductViewStorage();
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const filteredRecentlyViewed = recentlyViewed.filter(
    (item) => !removedIds.has(item.id),
  );

  // Get IDs of items currently displayed in recently viewed
  const displayedRecentlyViewedIds = new Set(
    filteredRecentlyViewed.map((item) => item.id),
  );

  // Filter out featured items that are currently in recently viewed
  const availableFeaturedItems = featuredItems.filter(
    (item) => !displayedRecentlyViewedIds.has(item.id),
  );

  // Calculate how many featured items to display (max 5 total items)
  const maxRecentlyViewed = Math.min(filteredRecentlyViewed.length, 2);
  const numberOfFeaturedToShow = Math.min(
    availableFeaturedItems.length,
    5 - maxRecentlyViewed,
  );
  const displayedFeaturedItems = availableFeaturedItems.slice(
    0,
    numberOfFeaturedToShow,
  );

  const webElement = {
    elementType: ElementType.Entry,
    htmlId: "recent-search-clear-button",
    location: location,
    name: `${location}-button`,
    text: "clear",
  };
  const { elementClicked: clearClicked, elementViewed: clearViewed } =
    useElementEvents({ correlationId: getCorrelationID(), webElement });

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const handleRemove = (
    e: React.MouseEvent<HTMLButtonElement>,
    productId: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    clearClicked();
    setRemovedIds((prev) => new Set(prev).add(productId));
    removeProductView(productId);
  };

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      clearViewed();
    }
  }, [inView, clearViewed]);

  return (
    <MainContent>
      <div className="w-full flex flex-col gap-4">
        {filteredRecentlyViewed && filteredRecentlyViewed.length > 0 && (
          <div className="border-b border-putty-400 px-4 mt-4">
            <div className="flex flex-row items-center gap-1">
              <Icon
                type="clock"
                svgProps={{ width: 24, height: 24 }}
                pathProps={{ fill: "#B4B1A8" }}
              />
              <span className="elm-p2 text-putty-600 font-bold">
                RECENTLY VIEWED
              </span>
            </div>
            <ul className="list-none !p-0 my-0">
              {filteredRecentlyViewed.map((item, index) => (
                <li key={item.id} className="m-0 py-2 flex justify-between">
                  <ProductSearchListItem
                    id={item.id}
                    productName={item.productName}
                    productCategory={item.productCategory}
                    partner={item.partner}
                    partnerLogoUrl={item.partnerLogoUrl}
                    subtype={FormatSubType.SearchRecent}
                    viewedSince={item.viewedSince}
                    timeToComplete={item.timeToComplete}
                    href={item.href}
                    index={index}
                    location={location}
                  />
                  <button
                    ref={inViewRef}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => handleRemove(e, item.id)}
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <Close
                      pathProps={{
                        fill: "#B4B1A8",
                      }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {displayedFeaturedItems && displayedFeaturedItems.length > 0 && (
          <div className="border-b border-putty-400 px-4">
            <div className="flex flex-row items-center gap-1">
              <Icon
                type="trending-up"
                svgProps={{ width: 24, height: 24 }}
                pathProps={{ fill: "#B4B1A8" }}
              />
              <span className="elm-p2 text-putty-600 font-bold">
                FEATURED PROGRAMS
              </span>
            </div>
            <ul className="list-none !p-0 my-0">
              {displayedFeaturedItems.map((item, index) => (
                <li key={item.id} className="m-0 py-2">
                  <ProductSearchListItem
                    id={item.id}
                    productName={item.productName}
                    productCategory={item.productCategory}
                    partner={item.partner}
                    partnerLogoUrl={item.partnerLogoUrl}
                    subtype={FormatSubType.SearchRecommendation}
                    viewedSince={item.viewedSince}
                    timeToComplete={item.timeToComplete}
                    href={item.href}
                    index={index}
                    location={location}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Footer>
        <span className="w-full h-[46px] flex items-center justify-center elm-p2 text-center text-primary-500 text-balance font-bold">
          Start typing to search 4,000+ learning opportunities
        </span>
      </Footer>
    </MainContent>
  );
};

export default DefaultState;
